import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  TextField,
  Chip,
  Avatar,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useSiteVisit } from '../../contexts/SiteVisitContext';
import { useLoader } from '../../contexts/LoaderContext';
import DateTimeRangePicker from '../../components/common/DateTimeRangePicker';
import dayjs from 'dayjs';

const RescheduleSiteVisit = ({ open, onClose, siteVisit, onSuccess }) => {
  
  const [newStartDateTime, setNewStartDateTime] = useState(
    siteVisit?.calendar_item?.start_time ? siteVisit.calendar_item.start_time : dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss')
  );
  const [newEndDateTime, setNewEndDateTime] = useState(
    siteVisit?.calendar_item?.end_time ? siteVisit.calendar_item.end_time : dayjs().add(1, 'hour').tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss')
  );
  const [reason, setReason] = useState('');
  
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { rescheduleSiteVisit } = useSiteVisit();
  const { showLoader, hideLoader } = useLoader();

  // Initialize date state when modal opens or siteVisit changes
  useEffect(() => {
    if (open && siteVisit) {
      const currentDate = siteVisit?.calendar_item?.start_time ? 
        siteVisit.calendar_item.start_time : 
        dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss');
      
      const endDate = siteVisit?.calendar_item?.end_time ? 
        siteVisit.calendar_item.end_time : 
        dayjs(currentDate).add(1, 'hour').tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss');
      
      setNewStartDateTime(currentDate);
      setNewEndDateTime(endDate);
    }
  }, [open, siteVisit]);

  

  const handleDateTimeChange = useCallback(({ startDateTime, endDateTime }) => {
    
    // The dates are already in IST format from DateTimeRangePicker
    const startDate = dayjs(startDateTime);
    const now = dayjs();

    setNewStartDateTime(startDateTime);
    setNewEndDateTime(endDateTime);
      
    // If the task is for a past date and status is still pending, suggest completed
    if (startDate.isBefore(now, 'day') && siteVisit?.status === 'pending') {
      setError('The visit is already scheduled for a past date. Please select a future date.');
    }
  }, [siteVisit?.status]);

  const handleSubmit = async () => {
    // Clear any previous errors
    setError('');
    
    // Validate form fields
    const validationErrors = [];
    
    if (!newStartDateTime) {
      validationErrors.push('Please select a new date and time');
    }

    if (!newEndDateTime) {
      validationErrors.push('Please select an end time');
    }

    if (!reason.trim()) {
      validationErrors.push('Please provide a reason for rescheduling');
    } else if (reason.trim().length < 20) {
      validationErrors.push('Reason must be at least 20 characters long');
    }

    if (newStartDateTime) {
       const startTime = dayjs(newStartDateTime);
       const now = dayjs();
       if (startTime.isBefore(now)) {
         validationErrors.push('Start time cannot be in the past');
       }
    }


    // Check if end time is before start time (convert to UTC for comparison)
    if (newStartDateTime && newEndDateTime) {
      const startTime = dayjs(newStartDateTime);
      const endTime = dayjs(newEndDateTime);
      if (endTime.isBefore(startTime)) {
        validationErrors.push('End time must be after start time');
      }
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }

    setIsSubmitting(true);
    showLoader();

    try {
      
      const payload = {
        start_time: newStartDateTime,
        end_time: newEndDateTime,
        reason: reason.trim()
      };

      console.log('Rescheduling with payload:', payload);
      await rescheduleSiteVisit(siteVisit.id, payload);
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error rescheduling visit:', error);
      
      // Handle different types of errors gracefully
      let errorMessage = 'Failed to reschedule visit. Please try again.';
      
      if (error.response?.data) {
        // Handle API response errors
        const responseData = error.response.data;
        
        if (responseData.non_field_errors) {
          errorMessage = Array.isArray(responseData.non_field_errors) 
            ? responseData.non_field_errors.join(', ') 
            : responseData.non_field_errors;
        } else if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.error) {
          errorMessage = responseData.error;
        } else if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData.errors) {
          // Handle field-specific errors
          const fieldErrors = Object.values(responseData.errors).flat();
          errorMessage = fieldErrors.join(', ');
        }
      } else if (error.message) {
        // Handle JavaScript errors
        errorMessage = error.message;
      } else if (error.isAuthError) {
        // Handle authentication errors
        errorMessage = 'Authentication error. Please log in again.';
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  const handleClose = () => {
    // Reset state when modal closes
    if (siteVisit?.calendar_item?.start_time) {
      const currentDate = siteVisit.calendar_item.start_time;
      const endDate = siteVisit?.calendar_item?.end_time ? 
        siteVisit.calendar_item.end_time : 
        dayjs(currentDate).add(1, 'hour').tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss');
      setNewStartDateTime(currentDate);
      setNewEndDateTime(endDate);
    } else {
      setNewStartDateTime(dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss'));
      setNewEndDateTime(dayjs().add(1, 'hour').tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss'));
    }
    setReason('');
    setError('');
    onClose();
  };

  const getLeadInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ScheduleIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Reschedule Site Visit
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {/* Current Visit Details - Redesigned */}
        <Box sx={{ 
          mb: 4,
          p: 3,
          borderRadius: 2,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          {/* Status Chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Status:
            </Typography>
            <Chip 
              label={siteVisit?.status_verbose || 'Unknown'} 
              size="small"
              color="primary"
              variant="outlined"
              sx={{ 
                fontSize: '0.75rem',
                height: '20px'
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', mt: 2, gap: 3, flexWrap: 'wrap' }}>
            {/* Lead Information */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'grey.200',
              minWidth: 200
            }}>
              <Avatar sx={{ 
                bgcolor: 'primary.main',
                width: 40,
                height: 40,
                fontSize: '0.875rem'
              }}>
                {getLeadInitials(siteVisit?.lead?.name)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {siteVisit?.lead?.name || 'Unknown Lead'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <PhoneIcon fontSize="small" color="action" sx={{ fontSize: '0.75rem' }} />
                  <Typography variant="caption" color="text.secondary">
                    {siteVisit?.lead?.mobile || siteVisit?.lead?.contact || 'No contact'}
                  </Typography>
                </Box>
                {siteVisit?.lead?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                    <EmailIcon fontSize="small" color="action" sx={{ fontSize: '0.75rem' }} />
                    <Typography variant="caption" color="text.secondary">
                      {siteVisit.lead.email}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Project Information */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'grey.200',
              minWidth: 200
            }}>
              <BusinessIcon color="primary" sx={{ fontSize: '1.5rem' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {siteVisit?.project?.name || 'Unknown Project'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Property Visit
                </Typography>
              </Box>
            </Box>

            {/* Current Schedule */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'grey.200',
              minWidth: 200
            }}>
              <ScheduleIcon color="warning" sx={{ fontSize: '1.5rem' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {siteVisit?.calendar_item?.start_time ? 
                    dayjs(siteVisit.calendar_item.start_time).tz('Asia/Kolkata').format('MMM DD, YYYY') : 
                    'Not scheduled'
                  }
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {siteVisit?.calendar_item?.start_time ? 
                    dayjs(siteVisit.calendar_item.start_time).tz('Asia/Kolkata').format('h:mm A') : 
                    'No time set'
                  }
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* New Schedule Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: 'text.primary',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <ScheduleIcon fontSize="small" color="success" />
            New Schedule
          </Typography>
          <DateTimeRangePicker
            initialStartDateTime={newStartDateTime}
            initialEndDateTime={newEndDateTime}
            onChange={handleDateTimeChange}
          />
        </Box>

        {/* Reason for Rescheduling */}
        <Box>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: 'text.primary',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            Reason for Rescheduling
          </Typography>
          <TextField
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Please provide a detailed reason for rescheduling this visit (minimum 20 characters)..."
            variant="outlined"
            error={reason.trim().length > 0 && reason.trim().length < 20}
            helperText={
              reason.trim().length > 0 && reason.trim().length < 20
                ? `Please provide at least 20 characters (${reason.trim().length}/20)`
                : `${reason.trim().length}/20 characters minimum`
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.875rem'
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              width: '100%',
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
                lineHeight: 1.4
              }
            }}
            onClose={() => setError('')}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Unable to Reschedule Visit
              </Typography>
              <Typography variant="body2" sx={{ color: 'error.dark' }}>
                {error}
              </Typography>
            </Box>
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          color="inherit"
          disabled={isSubmitting}
          sx={{ 
            textTransform: 'none',
            borderRadius: 2,
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting || !newStartDateTime || !newEndDateTime || !reason.trim() || reason.trim().length < 20}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ 
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            fontWeight: 600,
            minWidth: 140
          }}
        >
          {isSubmitting ? 'Rescheduling...' : 'Reschedule Visit'}
        </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleSiteVisit; 